import useSWR from "swr";
import SectionHeader from "@/components/SectionHeader";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { Wind, Droplets, CloudRain, ThermometerSun } from "lucide-react";
import { windDirection } from "@/lib/weatherUtils";

const fetcher = () => api.get("/forecast").then((r) => r.data);
const WEEK = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];

function dayLabel(iso, idx) {
  if (idx === 0) return "Oggi";
  if (idx === 1) return "Domani";
  const d = new Date(iso + "T12:00:00");
  return WEEK[d.getDay()];
}

function fmtHour(iso) {
  return new Date(iso).toLocaleTimeString("it-IT", { timeZone: "Europe/Rome", hour: "2-digit", minute: "2-digit" });
}

export default function Previsioni() {
  const { data, isLoading, error } = useSWR("forecast", fetcher, { refreshInterval: 30 * 60_000 });

  return (
    <div data-testid="page-previsioni" className="pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeader
          eyebrow="Previsioni"
          title="Palestrina, prossimi 5 giorni."
          subtitle="Previsioni meteorologiche fornite da OpenWeatherMap e centrate sulle coordinate della mia stazione (41.83° N, 12.89° E)."
        />

        {isLoading && <p className="text-slate-500">Caricamento previsioni…</p>}
        {error && (
          <div className="metric-card p-6 border-red-200 bg-red-50/50" data-testid="forecast-error">
            <p className="text-sm text-red-700">Impossibile caricare le previsioni. Riprova tra qualche minuto.</p>
          </div>
        )}

        {data?.days && (
          <>
            <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-6 mb-14" data-testid="forecast-days">
              {data.days.slice(0, 6).map((d, i) => (
                <motion.div
                  key={d.date}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                  className="metric-card p-5 text-center"
                  data-testid={`forecast-day-${i}`}
                >
                  <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500">{dayLabel(d.date, i)}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{new Date(d.date + "T12:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}</p>
                  {d.icon && (
                    <img
                      src={`https://openweathermap.org/img/wn/${d.icon}@2x.png`}
                      alt={d.desc}
                      className="h-20 w-20 mx-auto -my-2"
                    />
                  )}
                  <p className="text-xs text-slate-600 mt-1 min-h-8">{d.desc}</p>
                  <div className="mt-3 flex items-baseline justify-center gap-2">
                    <span className="font-display text-3xl text-[#0B132B]">{Math.round(d.t_max)}°</span>
                    <span className="text-sm text-slate-400">{Math.round(d.t_min)}°</span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-1 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-1 justify-center"><CloudRain className="h-3 w-3 text-[#0EA5E9]" />{d.rain} mm</span>
                    <span className="inline-flex items-center gap-1 justify-center"><Wind className="h-3 w-3 text-[#22C55E]" />{Math.round(d.wind_max)} km/h</span>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Hourly forecast 48h */}
            <SectionHeader eyebrow="Prossime 48 ore" title="Andamento orario." />
            <div className="metric-card p-4 md:p-6 overflow-x-auto" data-testid="forecast-hourly">
              <div className="flex gap-3 min-w-max pb-2">
                {data.hourly.map((h, i) => (
                  <div key={i} className="w-28 flex-shrink-0 rounded-2xl bg-slate-50 border border-slate-100 p-3 text-center">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{fmtHour(h.ts)}</p>
                    {h.icon && (
                      <img src={`https://openweathermap.org/img/wn/${h.icon}.png`} alt={h.desc} className="h-12 w-12 mx-auto -my-1" />
                    )}
                    <p className="font-display text-2xl text-[#0B132B] leading-tight">{Math.round(h.temp)}°</p>
                    <p className="text-[10px] text-slate-500 mt-1 flex items-center gap-1 justify-center">
                      <Droplets className="h-2.5 w-2.5 text-[#0EA5E9]" />{h.humidity}%
                    </p>
                    <p className="text-[10px] text-slate-500 flex items-center gap-1 justify-center">
                      <Wind className="h-2.5 w-2.5 text-[#22C55E]" />{Math.round(h.wind)} {windDirection(h.wind_dir)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <p className="mt-6 text-xs text-slate-400 flex items-center gap-2">
              <ThermometerSun className="h-3.5 w-3.5" />
              Dati previsionali forniti da <a href="https://openweathermap.org" target="_blank" rel="noreferrer" className="underline hover:text-[#0EA5E9]">OpenWeatherMap</a> · aggiornati ogni 30 minuti.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
